import { Format } from './format'
import { getTenantConfig } from '../tenant'

// PLAN_PRICES_GENERATED_BY_SCRIPT
// Prices are keyed by tenant slug (e.g., 'hoxton-mix-uk', 'hoxton-mix-au')

// Static tenant data compiled at build time
export const STATIC_TENANT_DATA = {
  "hoxton-mix-uk": {
    "id": 1,
    "name": "Hoxton Mix",
    "slug": "hoxton-mix-uk",
    "vat_rate": 1.2,
    "country": "GB",
    "region": 1,
    "default_location": 1
  },
  "hoxton-mix-au": {
    "id": 2,
    "name": "Hoxton Mix AU",
    "slug": "hoxton-mix-au",
    "vat_rate": 1.1,
    "country": "AU",
    "region": 2,
    "default_location": 4
  }
}

const pricesByTenant = {
  "hoxton-mix-uk": {
    "daily": {},
    "hourly": {},
    "monthly": {
      "hot-desk-coworking": 40000,
      "private-office-8-10": 288000,
      "Virtual-Office-London-Mail-Forwarding": 3799,
      "Virtual-Office-London": 2299,
      "virtual-office-whatsapp-for-business-number": 499,
      "Virtual-Office-London-Mail-Forwarding-addon": 1500
    },
    "yearly": {
      "Virtual-Office-London-Mail-Forwarding": 37999,
      "Virtual-Office-London": 22999,
      "virtual-office-whatsapp-for-business-number": 4999,
      "Virtual-Office-London-Mail-Forwarding-addon": 15000
    }
  },
  "hoxton-mix-au": {
    "daily": {},
    "hourly": {},
    "monthly": {
      "hot-desk-coworking": 40000,
      "private-office-8-10": 288000,
      "Virtual-Office-London-Mail-Forwarding": 7000,
      "Virtual-Office-London": 4900,
      "Virtual-Office-London-Mail-Forwarding-addon": 2100
    },
    "yearly": {
      "Virtual-Office-London-Mail-Forwarding": 70000,
      "Virtual-Office-London": 49000,
      "Virtual-Office-London-Mail-Forwarding-addon": 21000
    }
  }
}

// Additional pricing constants (not from API)
const additionalPricing = {
  "mailServices": {
    "standardLetterForwarding": 250,
    "largeLetterForwarding": 800,
    "scanContents": 100
  },
  "callForwarding": {
    "ukLandline": 4,
    "ukMobile": 7,
    "ukPremiumMobile": 64,
    "ukPersonalNumbers": 90,
    "ukSpecialServices": 7
  },
  "tide": {
    "cashbackAmount": 20000,
    "minimumBalance": 100
  },
  "crunch": {
    "vatFilingMonthly": 400,
    "accountantConsultation": 2450,
    "promoCreditAmount": 1000,
    "companyFormationPrice": 5000
  },
  "phoneService": {
    "monthlyOnYearly": 582
  }
}

// Helper to get tenant slug from cache (defaults to current location)
const getTenantSlug = (slugOrPath?: string): string => {
  try {
    const config = getTenantConfig(slugOrPath)
    return config.slug
  } catch (e) {
    // If tenant cache not initialized, default to UK
    return 'hoxton-mix-uk'
  }
}

// Lazy getter for additional pricing - only called when accessed
let cachedPricing: any = null

const getFormattedAdditionalPricing = () => {
  if (cachedPricing) return cachedPricing

  const formatted: any = {}
  const tenantSlug = getTenantSlug()

  // Mail services
  formatted.mailServices = {
    standardLetterForwarding: Format.money(additionalPricing.mailServices.standardLetterForwarding, tenantSlug),
    largeLetterForwarding: Format.money(additionalPricing.mailServices.largeLetterForwarding, tenantSlug),
    scanContents: Format.money(additionalPricing.mailServices.scanContents, tenantSlug),
  }

  // Call forwarding
  formatted.callForwarding = {
    ukLandline: Format.money(additionalPricing.callForwarding.ukLandline, tenantSlug),
    ukMobile: Format.money(additionalPricing.callForwarding.ukMobile, tenantSlug),
    ukPremiumMobile: Format.money(additionalPricing.callForwarding.ukPremiumMobile, tenantSlug),
    ukPersonalNumbers: Format.money(additionalPricing.callForwarding.ukPersonalNumbers, tenantSlug),
    ukSpecialServices: Format.money(additionalPricing.callForwarding.ukSpecialServices, tenantSlug),
  }

  // Tide
  formatted.tide = {
    cashbackAmount: Format.moneyShort(additionalPricing.tide.cashbackAmount, tenantSlug),
    minimumBalance: Format.moneyShort(additionalPricing.tide.minimumBalance, tenantSlug),
  }

  // Crunch
  formatted.crunch = {
    vatFilingMonthly: Format.money(additionalPricing.crunch.vatFilingMonthly, tenantSlug),
    accountantConsultation: Format.money(additionalPricing.crunch.accountantConsultation, tenantSlug),
    promoCreditAmount: Format.moneyShort(additionalPricing.crunch.promoCreditAmount, tenantSlug),
    companyFormationPrice: Format.moneyShort(additionalPricing.crunch.companyFormationPrice, tenantSlug),
  }

  // Phone service
  formatted.phoneService = {
    monthlyOnYearly: Format.money(additionalPricing.phoneService.monthlyOnYearly, tenantSlug),
  }

  cachedPricing = formatted
  return formatted
}

// Use a getter so it's only called when accessed, not at module load time
export const pricing = new Proxy({}, {
  get: (target, prop) => {
    const formatted = getFormattedAdditionalPricing()
    return formatted[prop]
  }
})

const getFormattedPrices = (
  duration: 'monthly' | 'yearly' | 'hourly' | 'daily',
  tenantSlugOrPath?: string,
) => {
  const tenantSlug = getTenantSlug(tenantSlugOrPath)
  const prices = pricesByTenant[tenantSlug as keyof typeof pricesByTenant] || pricesByTenant['hoxton-mix-uk']

  return Object.entries(prices[duration]).reduce(
    (formattedPrices, [plan, price]) => {
      formattedPrices[plan] = Format.money(price, tenantSlug)
      return formattedPrices
    },
    {} as { [key: string]: string },
  )
}

const getPlanPriceHelper = (tenantSlugOrPath?: string) => {
  const tenantSlug = getTenantSlug(tenantSlugOrPath)
  const prices = pricesByTenant[tenantSlug as keyof typeof pricesByTenant] || pricesByTenant['hoxton-mix-uk']

  return {
    daily: getFormattedPrices('daily', tenantSlug),
    hourly: getFormattedPrices('hourly', tenantSlug),
    monthly: getFormattedPrices('monthly', tenantSlug),
    monthlyOnYearlyPlan: (plan: string) => {
      const yearlyPrice =
        Number(
          getFormattedPrices('yearly', tenantSlug)[plan].replace(/[^0-9.-]+/g, ''),
        ) * 100
      const priceParts = Format.money(Math.round(yearlyPrice / 12), tenantSlug)?.split('.')
      const number = parseInt(priceParts[0].replace(/[£$]/, '')) * 100
      const decimal = parseFloat(priceParts[1]) / 100
      const roundUp = decimal >= 0.5

      if (roundUp) {
        return `Under ${Format.money(number + 100, tenantSlug)?.split('.')[0]}`
      }
      return `${Format.money(number, tenantSlug)?.split('.')[0]}`
    },
    dailyOnYearlyPlan: (plan: string) => {
      const yearlyPrice =
        Number(
          getFormattedPrices('yearly', tenantSlug)[plan].replace(/[^0-9.-]+/g, ''),
        ) * 100

      return Math.floor(yearlyPrice / 360)
    },
    monthlyOnYearlyPlanFloat: (plan: string) => {
      const yearlyPrice = Number(
        getFormattedPrices('yearly', tenantSlug)[plan].replace(/[^0-9.-]+/g, ''),
      )
      return Math.round(yearlyPrice / 12).toFixed(2)
    },
    yearly: getFormattedPrices('yearly', tenantSlug),
  }
}

// Lazy-initialized default planPrice (UK tenant)
let _cachedPlanPrice: ReturnType<typeof getPlanPriceHelper> | null = null
export const planPrice = new Proxy({} as ReturnType<typeof getPlanPriceHelper>, {
  get(_, prop) {
    if (!_cachedPlanPrice) _cachedPlanPrice = getPlanPriceHelper()
    return (_cachedPlanPrice as any)[prop]
  }
})

// For server-side or explicit tenant specification
// Pass tenant slug (e.g., 'au', 'uk') or path (e.g., '/au/checkout')
// Cached per resolved tenant: prices are baked in at build time, so each
// tenant's formatted maps only need building once. Keyed by the resolved
// slug (not the raw argument) so a no-arg call follows the current URL.
const _planPriceByTenant: Record<string, ReturnType<typeof getPlanPriceHelper>> = {}
export const getPlanPrice = (tenantSlugOrPath?: string) => {
  const tenantSlug = getTenantSlug(tenantSlugOrPath)
  return (_planPriceByTenant[tenantSlug] ??= getPlanPriceHelper(tenantSlugOrPath))
}
